import { z } from 'zod'

export const FontStorageSchema = z.enum(['resident', 'flash', 'ram', 'downloaded'])
export type FontStorage = z.infer<typeof FontStorageSchema>

export const GlyphRangeSchema = z.enum([
  'latin',
  'latin-ext',
  'cyrillic',
  'greek',
  'hebrew',
  'arabic',
  'thai',
  'cjk-s',
  'cjk-t',
  'japanese',
  'korean',
])
export type GlyphRange = z.infer<typeof GlyphRangeSchema>

export const PrinterFontSchema = z.object({
  id: z.string(),
  name: z.string(),
  storage: FontStorageSchema,
  type: z.enum(['bitmap', 'scalable']),
  glyphRanges: z.array(GlyphRangeSchema),
  sizeRange: z
    .object({
      minPt: z.number().positive(),
      maxPt: z.number().positive(),
    })
    .optional(),
})
export type PrinterFont = z.infer<typeof PrinterFontSchema>

export const MaterialPresetSchema = z.object({
  speed: z.number().positive(),
  darkness: z.number().int().min(0).max(30),
  ribbonType: z.enum(['wax', 'resin', 'wax-resin']).optional(),
})
export type MaterialPreset = z.infer<typeof MaterialPresetSchema>

const BasePrinterProfileSchema = z.object({
  id: z.string(),
  name: z.string(),
  brand: z.string(),
  model: z.string(),
  firmware: z.string(),
  dpi: z.union([z.literal(203), z.literal(300), z.literal(600)]),
  printWidthDots: z.number().int().positive(),
  mediaWidthMm: z.number().positive(),
  usablePrintWidthMm: z.number().positive(),
  fonts: z.array(PrinterFontSchema),
  /** Gates the FONT_SUBSTITUTION glyph-coverage warning only; wire bytes are
   *  always UTF-8 (^CI28) — see escape-zpl-text.ts (#137). */
  encoding: z.enum(['CP850', 'UTF8', 'UTF8-CJK']),
  media: z.object({
    gapType: z.enum(['gap', 'black-mark', 'notch', 'continuous']),
    gapValueMm: z.number().nonnegative(),
    orientation: z.union([z.literal(0), z.literal(180)]),
    homeOffsetDots: z.object({
      x: z.number().int(),
      y: z.number().int(),
    }),
  }),
  materialPresets: z.record(z.string(), MaterialPresetSchema).optional(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
})

export const PrinterProfileSchema = BasePrinterProfileSchema.refine(
  p => p.usablePrintWidthMm <= p.mediaWidthMm,
  { message: 'usablePrintWidthMm must be ≤ mediaWidthMm' }
)
export type PrinterProfile = z.infer<typeof PrinterProfileSchema>

export const PrinterProfileMetaSchema = BasePrinterProfileSchema.pick({
  id: true,
  name: true,
  brand: true,
  model: true,
  dpi: true,
  updatedAt: true,
})
export type PrinterProfileMeta = z.infer<typeof PrinterProfileMetaSchema>
